import React, { useEffect, useMemo, useState } from "react";
import { FaEye, FaDownload, FaLock } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson, appFetchBlob } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";
import FiltroLotes from "./FiltroLotes";
import VerLotes from "./VerLotes"; // <-- agregado

/**
 * ListaDeLotes
 * - Backend: GET /flpa/api/loteTarjeta/list?page=&size=&id=&estadoId=&fechaDesde=&fechaHasta=
 * - Acepta distintos shapes de respuesta ({items}|{content}|{list}) y nombres de campos.
 */
export default function ListaDeLotes() {
  // datos
  const [lotes, setLotes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [totalBackend, setTotalBackend] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [lastUrl, setLastUrl] = useState("");

  // descargas
  const [descargandoId, setDescargandoId] = useState(null);

  // cerrar lote
  const [cerrandoId, setCerrandoId] = useState(null);
  const [notif, setNotif] = useState({ kind: "", text: "" }); // kind: 'ok' | 'warn' | 'info'
  const showNotif = (kind, text) => setNotif({ kind, text });

  // filtros (UI → solo aplican al “Aplicar Filtros”)
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => {
    setMostrarFiltros(false);
    document.activeElement?.blur();
  }, mostrarFiltros);

  // { idLote, estado, fechaDesde, fechaHasta }
  const [filtros, setFiltros] = useState({});

  // paginación (UI 1-based)
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(30);

  // vista de detalle (UI)
  const [mostrarVerLote, setMostrarVerLote] = useState(false); // <-- agregado
  const [loteSeleccionado, setLoteSeleccionado] = useState(null); // <-- agregado

  const API_LIST = apiRoutes?.lotes?.list || null;

  const hasActiveFilters = useMemo(() => {
    const f = filtros || {};
    return !!(f.idLote || f.estado || f.fechaDesde || f.fechaHasta);
  }, [filtros]);

  // --- Helpers para request ---
  const buildParams = (page0, size, f) => {
    const p = new URLSearchParams();
    // paginación 0-based para backend
    if (page0 !== undefined) p.append("page", page0);
    if (size !== undefined) p.append("size", size);

    // mapeo de nombres → lo que espera el backend
    if (f?.idLote) p.append("id", String(f.idLote).trim());
    if (f?.estado) p.append("estadoId", String(f.estado).trim());
    if (f?.fechaDesde) p.append("fechaDesde", f.fechaDesde); // datetime-local ISO-like
    if (f?.fechaHasta) p.append("fechaHasta", f.fechaHasta);

    return p;
  };

  // Util: obtener arrays/total de distintas formas de payload
  const extractArray = (json) => {
    if (!json || typeof json !== "object") return [];
    if (Array.isArray(json.items)) return json.items;
    if (Array.isArray(json.content)) return json.content;
    if (Array.isArray(json.list)) return json.list;
    return [];
  };
  const extractTotal = (json, itemsLen) => {
    if (!json || typeof json !== "object") return itemsLen;
    if (typeof json.totalElements === "number") return json.totalElements;
    if (typeof json.total === "number") return json.total;
    if (json.pagination && typeof json.pagination.total === "number")
      return json.pagination.total;
    return itemsLen;
  };

  // page: 1-based (UI). Internamente mandamos 0-based.
  const fetchLotes = async (
    page = paginaActual,
    size = porPagina,
    filtrosOverride = null,
  ) => {
    if (!API_LIST) {
      setErrorMsg("Ruta API no configurada para lotes.");
      setCargando(false);
      return;
    }

    const f = filtrosOverride ?? filtros;
    setCargando(true);
    setErrorMsg("");
    showNotif("", "");

    try {
      const params = buildParams(page - 1, size, f);
      const url = `${API_LIST}?${params.toString()}`;
      setLastUrl(url);

      const json = await appFetchJson(url);
      const raw = extractArray(json);
      const total = extractTotal(json, raw.length);

      const mapped = raw.map((it) => ({
        id: it.id ?? it.idLote ?? it.loteId ?? null,
        dataFechaLote: it.dataFechaLote ?? it.fechaLote ?? it.loteFecha ?? null,
        estadoNombre: it.estadoNombre ?? it.estado ?? it.estadoLote ?? "",
      }));

      setLotes(mapped);
      setTotalBackend(typeof total === "number" ? total : mapped.length);
    } catch (e) {
      const status = e?.status
        ? ` [${e.status}${e.statusText ? " " + e.statusText : ""}]`
        : "";
      setErrorMsg(`No se pudo consultar lotes${status}.`);
      setLotes([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce("ListaDeLotes:init")) return;
    fetchLotes(1, porPagina);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // paginación
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));
  const startIndex = (paginaActual - 1) * porPagina;
  const endIndex = Math.min(startIndex + porPagina, totalBackend);
  const indiceInicio = totalBackend === 0 ? 0 : startIndex + 1;
  const indiceFin = endIndex;

  const cambiarPagina = (n) => {
    const next = Math.min(Math.max(1, n), totalPaginas);
    setPaginaActual(next);
    fetchLotes(next, porPagina);
  };

  const manejarCambioCantidad = (e) => {
    const nueva = Number(e.target.value);
    setPorPagina(nueva);
    setPaginaActual(1);
    fetchLotes(1, nueva);
  };

  // Aplicar filtros (solo al hacer click)
  const aplicarFiltros = (vals) => {
    const f = {
      idLote: (vals.idLote || "").trim(),
      estado: vals.estado || "",
      fechaDesde: vals.fechaDesde || "",
      fechaHasta: vals.fechaHasta || "",
    };
    setFiltros(f);
    setPaginaActual(1);
    fetchLotes(1, porPagina, f);

    // 👇 cerrar automáticamente el drawer al aplicar
    setMostrarFiltros(false);
  };

  const limpiarSeleccion = () => {
    const f = {};
    setFiltros(f);
    setPaginaActual(1);
    fetchLotes(1, porPagina, f);
  };

  // Chips (muestran lo aplicado; con remove por chip)
  const removeChip = (key) => {
    const f = { ...filtros };
    delete f[key];
    // normalizaciones
    if (key === "idLote") f.idLote = "";
    if (key === "estado") f.estado = "";
    if (key === "fechaDesde") f.fechaDesde = "";
    if (key === "fechaHasta") f.fechaHasta = "";
    setFiltros(f);
    setPaginaActual(1);
    fetchLotes(1, porPagina, f);
  };

  // Parseo/format de fecha robusto
  const parseToDate = (val) => {
    if (val == null) return null;
    if (typeof val === "number") return new Date(val);
    const s = String(val);
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return new Date(s); // ISO-like
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
      return new Date(s.replace(" ", "T"));
    }
    const n = Number(s);
    if (!Number.isNaN(n) && s.trim() !== "") return new Date(n);
    return new Date(s);
  };

  const fmt = (val) => {
    try {
      const d = parseToDate(val);
      if (!d || isNaN(d.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${pad(d.getFullYear())} ${pad(
        d.getHours(),
      )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return String(val) ?? "—";
    }
  };

  // ===== Descarga TXT =====
  const lpad = (v, len, ch = "0") => {
    let s = String(v ?? "");
    while (s.length < len) s = ch + s;
    return s;
  };

  const descargarArchivo = async (row) => {
    const id = row.id ?? row.idLote ?? row.loteId;
    if (!id) {
      setErrorMsg("No se encontró el Id de Lote para descargar.");
      return;
    }
    const url = apiRoutes?.lotes?.generarArchivo?.(id);
    if (!url) {
      setErrorMsg("Ruta API no configurada para generar archivo.");
      return;
    }

    setDescargandoId(String(id));
    setLastUrl(url);
    setErrorMsg("");

    try {
      const { blob, filename } = await appFetchBlob(url, {
        accept: "text/plain",
      },
  {
    showLoading: true,   // 👈 esto activa el overlay "Procesando..."
  });
      const name = filename || `TARBANCO_${lpad(id, 5)}.txt`;

      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e) {
      setErrorMsg(`No se pudo generar el archivo. ${e?.message ?? ""}`);
    } finally {
      setDescargandoId(null);
    }
  };

  // ===== Helpers de estado =====
  const isCerrado = (row) => {
    const s = (row?.estadoNombre ?? row?.estado ?? "").toString().toUpperCase();
    return s.includes("CERRAD"); // "CERRADO" / "CERRADA"
  };

  // ===== Cerrar Lote =====
  const cerrarLote = async (row) => {
    const id = row?.id ?? row?.idLote ?? row?.loteId;
    if (!id) {
      setErrorMsg("No se encontró el Id de Lote para cerrar.");
      return;
    }
    const url = apiRoutes?.lotes?.cerrar?.(id);
    if (!url) {
      setErrorMsg("Ruta API no configurada para cerrar lote.");
      return;
    }

    setCerrandoId(String(id));
    setLastUrl(url);
    showNotif("", "");
    setErrorMsg("");

    try {
      await appFetchJson(url, { method: "POST" });
      showNotif("ok", `Lote ${id} cerrado correctamente.`);
      // refrescar la página actual con filtros vigentes
      fetchLotes(paginaActual, porPagina);
    } catch (e) {
      const st = e?.status || 0;
      if (st === 409) {
        showNotif("warn", `El lote ${id} ya estaba cerrado.`);
      } else if (st === 404) {
        showNotif("warn", `El lote ${id} no existe.`);
      } else if (st === 403) {
        setErrorMsg("No tenés permiso para cerrar lotes [403].");
      } else {
        setErrorMsg(`No se pudo cerrar el lote${st ? ` [${st}]` : ""}.`);
      }
    } finally {
      setCerrandoId(null);
    }
  };

  // === abrir/cerrar detalle ===
  const abrirDetalle = (row) => {
    const id = row.id ?? row.idLote ?? row.loteId ?? null;
    setLoteSeleccionado({ idLote: id }); // si luego necesitás detalles, pasalos acá
    setMostrarVerLote(true);
  };
  const cerrarDetalle = () => {
    setMostrarVerLote(false);
    setLoteSeleccionado(null);
  };

  return (
    <div className="page">
      <h1 className="page-title">📦 Lista de Lotes</h1>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="inline-flex items-center gap-2">
          <button
            className="btn-fil"
            onClick={() => setMostrarFiltros((v) => !v)}
          >
            {mostrarFiltros ? "Ocultar Filtros" : "Ver Filtros"}
          </button>

          {hasActiveFilters && (
            <button className="btn-fil ml-2" onClick={limpiarSeleccion}>
              Limpiar Selección
            </button>
          )}
        </div>
        {/* Drawer de filtros (se oculta cuando estás en la vista de detalle) */}
        {mostrarFiltros && !mostrarVerLote && (
          <FiltroLotes
            initial={filtros}
            onSubmit={aplicarFiltros}
            onClose={() => setMostrarFiltros(false)}
          />
        )}

        {/* Chips de filtros activos */}
        {!mostrarFiltros && (
          <div className="flex flex-wrap gap-2">
            {/* {filtros.idLote ? (
              <span className="chip">
                Id Lote: {filtros.idLote}
                <button className="chip-x" onClick={() => removeChip("idLote")}>×</button>
              </span>
            ) : null} */}
            {filtros.estado ? (
              <span className="chip">
                Estado: {filtros.estado}
                <button className="chip-x" onClick={() => removeChip("estado")}>
                  ×
                </button>
              </span>
            ) : null}
            {filtros.fechaDesde ? (
              <span className="chip">
                Desde: {filtros.fechaDesde.replace("T", " ")}
                <button
                  className="chip-x"
                  onClick={() => removeChip("fechaDesde")}
                >
                  ×
                </button>
              </span>
            ) : null}
            {filtros.fechaHasta ? (
              <span className="chip">
                Hasta: {filtros.fechaHasta.replace("T", " ")}
                <button
                  className="chip-x"
                  onClick={() => removeChip("fechaHasta")}
                >
                  ×
                </button>
              </span>
            ) : null}
          </div>
        )}
      </div>

      {/* Mostrar por página del PADRE — oculto cuando hay detalle */}
      {!mostrarVerLote && (
        <div className="control-block">
          <label htmlFor="pp" className="label-strong">
            Mostrar por página:
          </label>
          <select
            id="pp"
            value={porPagina}
            onChange={manejarCambioCantidad}
            className="select"
          >
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>
      )}

      {/* Error de consulta */}
      {errorMsg && (
        <div className="mt-2 mb-3 p-3 rounded bg-red-50 text-red-700 text-sm">
          <div className="font-semibold">Error</div>
          <div>{errorMsg}</div>
          {lastUrl ? (
            <div className="mt-1 opacity-80 break-all">
              Consulta: <code>{lastUrl}</code>
            </div>
          ) : null}
        </div>
      )}

      {/* Notificación funcional */}
      {notif.text ? (
        <div
          className={
            "mt-2 mb-3 p-3 rounded text-sm " +
            (notif.kind === "ok"
              ? "bg-green-50 text-green-700"
              : notif.kind === "warn"
                ? "bg-yellow-50 text-yellow-700"
                : "bg-blue-50 text-blue-700")
          }
        >
          <div className="font-semibold">
            {notif.kind === "ok"
              ? "OK"
              : notif.kind === "warn"
                ? "Atención"
                : "Info"}
          </div>
          <div>{notif.text}</div>
        </div>
      ) : null}

      {cargando ? (
        <div className="loading-text">Cargando lotes…</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="table-card">
              {/* thead del PADRE — se oculta cuando se abre el detalle */}
              {!mostrarVerLote && (
                <thead className="table-head">
                  <tr>
                    <th className="cell text-left">Id Lote</th>
                    <th className="cell text-left">Fecha Lote</th>
                    <th className="cell text-left">Estado</th>
                    <th className="cell-center">Acción</th>
                  </tr>
                </thead>
              )}

              <tbody>
                {/* Detalle colocado inmediatamente debajo del header del padre */}
                {mostrarVerLote && (
                  <tr className="bg-transparent">
                    <td colSpan={4} className="p-0">
                      <VerLotes
                        onVolver={cerrarDetalle}
                        lote={loteSeleccionado}
                      />
                    </td>
                  </tr>
                )}

                {/* Filas del listado solo si NO hay detalle */}
                {!mostrarVerLote && (
                  <>
                    {lotes.length === 0 ? (
                      <tr>
                        <td className="cell text-center" colSpan={4}>
                          Ningún ítem encontrado.
                        </td>
                      </tr>
                    ) : (
                      lotes.map((row, idx) => {
                        const rowId =
                          row.id ?? row.idLote ?? row.loteId ?? null;
                        const downloading =
                          String(descargandoId) === String(rowId);
                        const closing = String(cerrandoId) === String(rowId);
                        const cerrado = isCerrado(row);

                        return (
                          <tr
                            key={rowId ?? idx}
                            className={idx % 2 === 0 ? "row-alt" : "bg-white"}
                          >
                            <td className="cell">{rowId ?? "—"}</td>
                            <td className="cell">
                              {fmt(row.dataFechaLote ?? row.fechaLote)}
                            </td>
                            <td className="cell">
                              {row.estadoNombre ?? row.estado ?? "—"}
                            </td>
                            <td className="cell-center space-x-2">
                              <button
                                className="icon-btn"
                                title="Visualizar"
                                onClick={() => abrirDetalle(row)}
                              >
                                <FaEye />
                              </button>

                              <button
                                className={`icon-btn ${downloading ? "opacity-50 pointer-events-none" : ""}`}
                                title={
                                  downloading ? "Generando…" : "Generar Archivo"
                                }
                                onClick={() => descargarArchivo(row)}
                                disabled={downloading}
                              >
                                <FaDownload />
                              </button>

                              <button
                                className={`icon-btn ${closing || cerrado ? "opacity-50 pointer-events-none" : ""}`}
                                title={
                                  closing
                                    ? "Cerrando…"
                                    : cerrado
                                      ? "Ya cerrado"
                                      : "Cerrar Lote"
                                }
                                onClick={() => cerrarLote(row)}
                                disabled={closing || cerrado}
                              >
                                <FaLock />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Resumen + paginación del PADRE — ocultos cuando hay detalle */}
          {!mostrarVerLote && (
            <>
              <div className="mt-4 text-sm text-gray-600">
                {`${totalBackend.toLocaleString("es-ES")} item${
                  totalBackend !== 1 ? "s" : ""
                } encontrados, mostrando de ${indiceInicio} a ${indiceFin}.`}
              </div>

              <nav className="pager">
                <button
                  onClick={() => cambiarPagina(1)}
                  disabled={paginaActual === 1}
                  className={paginaActual === 1 ? "pager-off" : "pager-btn"}
                >
                  Primera
                </button>
                <button
                  onClick={() => cambiarPagina(paginaActual - 1)}
                  disabled={paginaActual === 1}
                  className={paginaActual === 1 ? "pager-off" : "pager-btn"}
                >
                  Anterior
                </button>
                {[
                  ...Array(Math.max(1, Math.ceil(totalBackend / porPagina))),
                ].map((_, i) => {
                  const num = i + 1;
                  if (
                    num === 1 ||
                    num === Math.ceil(totalBackend / porPagina) ||
                    (num >= paginaActual - 2 && num <= paginaActual + 2)
                  ) {
                    return (
                      <button
                        key={num}
                        onClick={() => cambiarPagina(num)}
                        className={
                          paginaActual === num ? "pager-active" : "pager-btn"
                        }
                      >
                        {num}
                      </button>
                    );
                  }
                  if (num === paginaActual - 3 || num === paginaActual + 3) {
                    return (
                      <span key={num} className="px-2 py-1">
                        …
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => cambiarPagina(paginaActual + 1)}
                  disabled={
                    paginaActual ===
                    Math.max(1, Math.ceil(totalBackend / porPagina))
                  }
                  className={
                    paginaActual ===
                    Math.max(1, Math.ceil(totalBackend / porPagina))
                      ? "pager-off"
                      : "pager-btn"
                  }
                >
                  Próxima
                </button>
                <button
                  onClick={() =>
                    cambiarPagina(
                      Math.max(1, Math.ceil(totalBackend / porPagina)),
                    )
                  }
                  disabled={
                    paginaActual ===
                    Math.max(1, Math.ceil(totalBackend / porPagina))
                  }
                  className={
                    paginaActual ===
                    Math.max(1, Math.ceil(totalBackend / porPagina))
                      ? "pager-off"
                      : "pager-btn"
                  }
                >
                  Última
                </button>
              </nav>
            </>
          )}
        </>
      )}
    </div>
  );
}
